/**
 * Wire types of the flow-web API: token jobs, their tracking status, ids and
 * tweets on the price chart. Mirrors `crates/flow-query/src/types/jobs.rs`
 * and `mod.rs`; conventions in `../types.ts`.
 */

import type { FlowWindow } from "./holders_supply";
import type { Mark } from "./shared";
import type { Coverage, EntrySource } from "./shared_profile";
import type { Signal } from "./signals_health";

/** GET /api/jobs */
export interface JobsResponse {
  tokens: TokenJob[];
  liquidity: ChainJob[];
  hooks: ChainJob[];
}

export interface TrackingStatus {
  name: string;
  enabled: boolean;
  /** the pause switch from the token list; `status` turns "paused" once token-flow has stopped */
  paused?: boolean;
  status: "starting" | "backfilling" | "live" | "retrying" | "paused" | "complete" | "stopped" | "error";
  start_block: number | null;
  last_block: number | null;
  last_success_at: number | null;
  last_error: string | null;
  updated_at: number;
}

export interface UsdSizeTiers {
  small_cents: number;
  big_cents: number;
  whale_cents: number;
}

export interface TokenJob {
  /** Absent until this token migrates from token-based tiers. */
  size_tiers_usd?: UsdSizeTiers;
  tracking?: TrackingStatus;
  /** Trailing 24h trade volume, unavailable before the first scan. */
  flow_24h_usd?: number;
  /** "token:{chain}:{token}" */
  id: string;
  chain: string;
  token: string;
  symbol: string;
  decimals: number;
  coverage: Coverage;
  first_block?: number;
  last_block?: number;
  last_timestamp?: number;
  counts: FlowCounts;
  mark?: Mark;
  holders_now?: number;
  entry_sources: EntrySource[];
  quotes: string[];
  urls: ExplorerTemplates;
}

export interface ExplorerTemplates {
  explorer_tx?: string;
  explorer_address?: string;
}

export interface FlowCounts {
  actions: number;
  trades: number;
  flagged: number;
  links: number;
  venues: number;
  holders: number;
  balances: number;
  accumulators: number;
  accumulating: number;
  v4_pools: number;
  rates: number;
}

export type ChainJobKind = "liquidity" | "hooks";

export interface ChainJob {
  /** "liquidity:{chain}" | "hooks:{chain}" */
  id: string;
  kind: ChainJobKind;
  chain: string;
  coverage: Coverage;
  last_block?: number;
  last_timestamp?: number;
  counts: Record<string, number>;
  available: boolean;
}

export type HeadSource = "rpc" | "newest_row" | "unknown";

export interface HeadInfo {
  block: number;
  source: HeadSource;
  lag_blocks?: number;
  lag_secs?: number;
}

export interface HoldersNow {
  count: number;
  any_balance: number;
  at: number;
  block: number;
  delta?: number;
  incomplete: boolean;
  alerted: boolean;
}

export interface AccumulatorCounts {
  evaluated: number;
  accumulating: number;
  oldest_evaluated_at?: number;
}

export interface VenueInfo {
  address: string;
  kind: string;
  since_block: number;
  /** Trades involving this venue, including multi-venue trades. */
  trades: number;
  /** Attributable USD from single-venue trades only. */
  volume_usd: number;
  lp_moves: number;
  pool_id?: string;
}

/** GET /api/jobs/{chain}/{token}/summary */
export interface JobSummary {
  job: TokenJob;
  mark?: Mark;
  holders?: HoldersNow;
  flow_24h: FlowWindow;
  accumulators: AccumulatorCounts;
  top_signals: Signal[];
  venues: VenueInfo[];
  head?: HeadInfo;
}

export const tokenJobId = (chain: string, token: string): string =>
  `token:${chain.toLowerCase()}:${token.toLowerCase()}`;

export const liquidityJobId = (chain: string): string => `liquidity:${chain.toLowerCase()}`;

export const hooksJobId = (chain: string): string => `hooks:${chain.toLowerCase()}`;

/** Allowed `bucket` values (seconds). */
export const BUCKETS = [60, 300, 900, 3600, 14400, 86400] as const;

export type Bucket = (typeof BUCKETS)[number];

// Project tweets on the price chart.
export type TweetKind = "official" | "popular";

export interface TweetsQuery {
  from: number;
  to: number;
}

export interface ChartTweet {
  id: string;
  project: string;
  kind: TweetKind;
  username: string;
  author_name: string;
  text: string;
  timestamp: number;
  likes: number;
  retweets: number;
  replies: number;
  quotes: number;
  score: number;
  fetched_at: number;
}

export interface TweetsResponse {
  status: "ready" | "not_configured" | "unavailable";
  tweets: ChartTweet[];
  truncated: boolean;
}
